import React, { useState, useMemo } from "react";
import { Search } from "lucide-react";
import { ShipPosition } from "../types/ShipPosition";
import { PortPosition } from "../types/PortPosition";

type Props = {
  marineData: ShipPosition[] | null;
  portData: PortPosition[] | null;
  onSelectLocation: (coords: {
    longitude: number;
    latitude: number;
    zoom: number;
  }) => void;
};

const SearchBar: React.FC<Props> = ({
  marineData,
  portData,
  onSelectLocation,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);

  const filteredResults = useMemo(() => {
    const lower = searchQuery.toLowerCase();
    if (!lower) return [];

    const ships = (marineData ?? [])
      .filter((ship) => ship.SHIPNAME.toLowerCase().includes(lower))
      .map((ship) => ({
        type: "ship" as const,
        name: ship.SHIPNAME,
        coords: {
          longitude: parseFloat(ship.LON),
          latitude: parseFloat(ship.LAT),
        },
      }));

    const ports = (portData ?? [])
      .filter(
        (port) =>
          port.CITY?.toLowerCase().includes(lower) ||
          port.STATE?.toLowerCase().includes(lower) ||
          port.COUNTRY?.toLowerCase().includes(lower)
      )
      .map((port) => ({
        type: "port" as const,
        name: `${port.CITY}, ${port.STATE}, ${port.COUNTRY}`,
        coords: {
          longitude: port.LONGITUDE,
          latitude: port.LATITUDE,
        },
      }));

    return [...ships, ...ports].slice(0, 10);
  }, [searchQuery, marineData, portData]);

  const handleSearch = () => {
    if (filteredResults.length === 0) return;

    const topResult = filteredResults[0];
    onSelectLocation({
      ...topResult.coords,
      zoom: 13,
    });
    setIsDropdownVisible(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleResultClick = (result: (typeof filteredResults)[number]) => {
    onSelectLocation({ ...result.coords, zoom: 13 });
    setSearchQuery(result.name);
    setIsDropdownVisible(false);
  };

  return (
    <div className="absolute top-20 right-4 z-10 w-[50%] max-w-sm md:left-1/2 md:-translate-x-1/2 md:right-auto">
      <div className="relative">
        <input
          type="text"
          placeholder="Search for ships or ports"
          className="w-full px-3 py-2 bg-white/10 backdrop-blur-md rounded-lg border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-blue-400"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setIsDropdownVisible(true);
          }}
          onKeyDown={handleKeyDown}
        />
        <button
          className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50"
          onClick={handleSearch}
        >
          <Search />
        </button>

        {isDropdownVisible && filteredResults.length > 0 && (
          <ul className="absolute mt-1 w-full max-h-60 overflow-auto bg-black/80 border border-white/10 rounded-lg shadow-lg backdrop-blur-md z-10 text-white">
            {filteredResults.map((result, index) => (
              <li
                key={`${result.type}-${index}`}
                className="px-4 py-2 hover:bg-white/10 cursor-pointer"
                onClick={() => handleResultClick(result)}
              >
                {result.type === "ship" ? "🚢" : "📍"} {result.name}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default SearchBar;
